document.addEventListener('DOMContentLoaded', () => {
  const logo = document.querySelector('#main-logo');
  const header = document.querySelector('.header');
  const homepageBannerSlideshow = document.querySelector('.slider-component-homepage');
  const swiperContainer = document.querySelector('.swiper-banners');
  const footer = document.querySelector('footer');

  const blockVisibilityMap = new WeakMap();

  const VISIBLE_THRESHOLD = 75;
  const SWIPER_UPDATE_DELAY_MS = 200;

  let swiperBanners = null;

  let cachedBlocksOutsideSlider = null;
  let cachedActiveSlideBlocks = [];
  let lastActiveSlide = null;
  let intersectionObserver = null;

  let rafId = null;
  let lastSecondaryState = null;

  function isSecondaryBlock(block) {
    return block?.dataset.secondaryStyle === 'true';
  }

  function getCachedBlocks() {
    if (cachedBlocksOutsideSlider === null) {
      const allBlocks = Array.from(document.querySelectorAll('[data-secondary-style]'));
      cachedBlocksOutsideSlider = allBlocks.filter(
        (block) => !block.closest('.swiper-banners')
      );
    }

    const activeSlide = document.querySelector('.swiper-banners .swiper-slide-active');
    const currentActiveSlide = activeSlide || null;

    if (currentActiveSlide !== lastActiveSlide) {
      const oldBlocks = cachedActiveSlideBlocks || [];

      cachedActiveSlideBlocks = currentActiveSlide
        ? Array.from(currentActiveSlide.querySelectorAll('[data-secondary-style]'))
        : [];

      lastActiveSlide = currentActiveSlide;

      if (intersectionObserver) {
        oldBlocks.forEach((block) => intersectionObserver.unobserve(block));
        cachedActiveSlideBlocks.forEach((block) => intersectionObserver.observe(block));
      }
    }

    return [...cachedBlocksOutsideSlider, ...cachedActiveSlideBlocks];
  }

  function getVisibilityForBlock(block) {
    const stored = blockVisibilityMap.get(block);
    if (stored) return stored;

    const rect = block.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
    const blockHeight = rect.height;

    const visiblePercentage =
      blockHeight > 0 && visibleHeight > 0
        ? (visibleHeight / blockHeight) * 100
        : 0;

    const scrollPosition = window.scrollY;
    const blockTop = rect.top + scrollPosition;

    return {
      isIntersecting: visiblePercentage > 0,
      visiblePercentage,
      blockTop,
      rect,
    };
  }

  function initIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      return null;
    }

    return new IntersectionObserver(
      (entries) => {
        const scrollPosition = window.scrollY;

        entries.forEach((entry) => {
          const block = entry.target;
          const rect = entry.boundingClientRect;
          const intersectionRect = entry.intersectionRect;

          const visibleHeight = Math.max(intersectionRect.height, 0);
          const blockHeight = rect.height;

          const visiblePercentage =
            blockHeight > 0 && visibleHeight > 0
              ? (visibleHeight / blockHeight) * 100
              : 0;

          const blockTop = rect.top + scrollPosition;

          blockVisibilityMap.set(block, {
            isIntersecting: entry.isIntersecting,
            visiblePercentage,
            blockTop,
            rect,
          });
        });

        scheduleHeaderUpdate();
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
  }

  function hasSecondaryStyleInView() {
    const blocks = getCachedBlocks();
    if (!blocks.length) return false;

    const scrollPosition = window.scrollY;
    const viewportTop = scrollPosition;

    const visibleBlocks = blocks
      .map((block) => {
        if (intersectionObserver) {
          const visibility = blockVisibilityMap.get(block);
          if (!visibility || !visibility.isIntersecting) return null;
          return {
            block,
            visiblePercentage: visibility.visiblePercentage,
            blockTop: visibility.blockTop,
          };
        } else {
          const visibility = getVisibilityForBlock(block);
          if (!visibility.isIntersecting) return null;
          return {
            block,
            visiblePercentage: visibility.visiblePercentage,
            blockTop: visibility.blockTop,
          };
        }
      })
      .filter(Boolean)
      .sort((a, b) => a.blockTop - b.blockTop);

    if (!visibleBlocks.length) return false;

    let currentBlock = null;
    let nextBlock = null;

    visibleBlocks.forEach((item) => {
      const { block, visiblePercentage, blockTop } = item;

      if (!currentBlock && visiblePercentage > 0) {
        currentBlock = block;
      }

      if (visiblePercentage >= VISIBLE_THRESHOLD && blockTop >= viewportTop) {
        const nextBlockVisibility = intersectionObserver 
          ? blockVisibilityMap.get(nextBlock)
          : getVisibilityForBlock(nextBlock);
        const nextBlockTop = nextBlockVisibility?.blockTop || Infinity;
        if (!nextBlock || blockTop < nextBlockTop) {
          nextBlock = block;
        }
      }
    });

    if (!currentBlock) return false;

    const currentIsSecondary = isSecondaryBlock(currentBlock);

    if (nextBlock) {
      const nextIsSecondary = isSecondaryBlock(nextBlock);
      if (currentIsSecondary === nextIsSecondary) {
        return currentIsSecondary;
      }
    }

    const targetBlock = nextBlock || currentBlock;
    return isSecondaryBlock(targetBlock);
  }

  function scheduleHeaderUpdate() {
    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
      rafId = null;

      const hasSecondary = hasSecondaryStyleInView();

      if (lastSecondaryState === hasSecondary) return;
      lastSecondaryState = hasSecondary;

      logo?.classList.toggle('hide', hasSecondary);
      header?.classList.toggle('white', hasSecondary);
      homepageBannerSlideshow?.classList.toggle('white', hasSecondary);
    });
  }

  function handleScroll() {
    if (intersectionObserver) return;
    scheduleHeaderUpdate();
  }

  function setupSwiperListeners(swiper) {
    if (swiperBanners) return;

    swiperBanners = swiper;

    const scheduleAfterTransition = () => {
      setTimeout(() => {
        scheduleHeaderUpdate();
      }, SWIPER_UPDATE_DELAY_MS);
    };

    swiperBanners.on('slideChange', scheduleAfterTransition);
    swiperBanners.on('realIndexChange', scheduleAfterTransition);
  }

  function initSwiperListener() {
    if (typeof window.swiper_banners !== 'undefined' && window.swiper_banners) {
      setupSwiperListeners(window.swiper_banners);
      return;
    }

    if (swiperContainer && swiperContainer.swiper) {
      setupSwiperListeners(swiperContainer.swiper);
      return;
    }

    if (typeof Swiper !== 'undefined' && swiperContainer) {
      const checkSwiperReady = () => {
        if (swiperContainer.swiper) {
          setupSwiperListeners(swiperContainer.swiper);
          return true;
        }
        return false;
      };

      if (checkSwiperReady()) {
        return;
      }

      const originalSwiper = Swiper;

      class InterceptedSwiper extends originalSwiper {
        constructor(...args) {
          super(...args);

          const target = args[0];
          const isBannerSwiper =
            target === '.swiper-banners' ||
            (typeof target === 'object' && target === swiperContainer);

          if (isBannerSwiper) {
            this.once('init', () => {
              setupSwiperListeners(this);
            });
          }
        }
      }

      if (!window.swiper_banners && window.Swiper === originalSwiper) {
        window.Swiper = InterceptedSwiper;
      }

      setTimeout(() => {
        if (!swiperBanners && checkSwiperReady()) {
          if (window.Swiper === InterceptedSwiper) {
            window.Swiper = originalSwiper;
          }
        }
      }, 500);
    }
  }

  intersectionObserver = initIntersectionObserver();

  // Toggle `footer-visible` class on the header when the footer enters/leaves the viewport.
  if (footer && header) {
    const footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        header.classList.toggle('footer-visible', entry.isIntersecting);
      },
      { threshold: 0 }
    );

    footerObserver.observe(footer);
  }

  if (intersectionObserver) {
    const blocks = getCachedBlocks();
    blocks.forEach((block) => {
      intersectionObserver.observe(block);
    });
  } else {
    window.addEventListener('scroll', handleScroll, { passive: true });
  }

  window.addEventListener('resize', scheduleHeaderUpdate);

  scheduleHeaderUpdate();

  initSwiperListener();

});